import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import { Redirect, useHistory } from "react-router-dom";

import { notify } from "@cayuse/shared-react.components.banner-notification";
import BasePadding from "@cayuse/shared-react.components.base-padding";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import { MilestoneForm, MilestoneProgress, TaskFormHeader } from "@cayuse/shared-react.components.task-form";
import { useTenantContext } from "@cayuse/shared-react.context.tenant";

import {
  AWARDS,
  PROJECTS,
  PROPOSALS,
  ROLE_SP_AWARD_ADMINISTRATOR,
  ROLE_SP_PROPOSAL_ADMINISTRATOR, ROLE_SP_SUBAWARD_ADMINISTRATOR,
  ROLE_SP_SYSTEM_ADMINISTRATOR, SUBAWARDS,
} from "globals/constants";
import { TASK_CATEGORIES } from "globals/constants/task";
import { useDictionary, useHasRoles } from "hooks";

import { redirectBackToTaskTab } from "../../utils";
import styles from "./styles.css";
import { useRecord } from "./useRecord";
import { buildBreadcrumbs, useShouldRedirectToCorrectTaskDetailsPage } from "./utils";

const ALLOW_TO_EDIT_ACTIONS_BY_ROLES = [
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_SUBAWARD_ADMINISTRATOR,
];

export function MilestonePage () {
	const history = useHistory();
	const dictionary = useDictionary("tasks");

	const ctx = useTenantContext();
	const {
		taskId,
		record,
		recordId,
		recordUrl,
		recordBreadcrumbs,
		submissionType,
	} = useRecord();

	const [ task, setTask ] = React.useState({});
	const [ isShowConfirmCancelModal, setShowConfirmCancelModal ] = React.useState(false);

	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin,
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isProposalAdmin,
		[ROLE_SP_SUBAWARD_ADMINISTRATOR]: isSubawardAdmin
	} = useHasRoles([
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_AWARD_ADMINISTRATOR,
		ROLE_SP_PROPOSAL_ADMINISTRATOR,
		ROLE_SP_SUBAWARD_ADMINISTRATOR
	]);

	const /**@type boolean*/isAdmin = isSystemAdmin
    || (submissionType === PROJECTS && (isAwardAdmin || isProposalAdmin || isSubawardAdmin))
    || (submissionType === PROPOSALS && isProposalAdmin)
    || (submissionType === AWARDS && isAwardAdmin)
    || (submissionType === SUBAWARDS && isSubawardAdmin);

	/**
   * Redirects back to the previous page.
   */
	const redirectBack = useCallback(() => {
		redirectBackToTaskTab(history, { submissionType, recordId, recordUrl });
	}, [ history, submissionType, recordId, recordUrl ]);

	const { redirectPath } = useShouldRedirectToCorrectTaskDetailsPage(task);
	if (redirectPath) {
		return <Redirect to={redirectPath} />;
	}

	const onAbortCancel = () => {
		setShowConfirmCancelModal(false);
	};

	const preparePayload = payload => {
		const url = `${window.location.origin}${recordUrl}`;
		if (url.includes("undefined")) {
			throw new Error("Incorrect URL. Please refresh page and try again.");
		}

		payload.url = url;
		payload.category = TASK_CATEGORIES.CAYUSE_SP_MILESTONE;
		payload.applicationHostUrl = `${window.location.protocol}//${window.location.hostname}/`;
		payload.variables = {
			projectId: record.projectId,
			submissionId: recordId,
			createdById: ctx.data.user.id,
			createdByName: ctx.data.person.fullName,
		};

		return payload;
	};

	const onSuccess = message => {
		notify(message, { type: "success" });
		redirectBack();
	};

	const onCancel = ({ unsavedChanges }) => {
		if (unsavedChanges) {
			setShowConfirmCancelModal(true);
		} else {
			redirectBack();
		}
	};

	return (
		<div className={styles.taskPage}>
			<SectionHeader
				header={<TaskFormHeader title={taskId ? task.name : dictionary.newMilestone} externalDueDate={task.dueOn} />}
				breadcrumbs={buildBreadcrumbs({
					record,
					recordUrl,
					recordBreadcrumbs,
					task,
					newTaskText: dictionary.newMilestone,
				})}
			/>
			<BasePadding>
				<div className={styles.taskFormWrapper}>
					<MilestoneForm
						id={taskId}
						submissionId={recordId}
						onEntityLoaded={setTask}
						onBeforeSuccess={() => {
							return new Promise(resolve => setTimeout(resolve, 1800));
						}}
						onConfirm={preparePayload}
						onSuccess={onSuccess}
						onCancel={onCancel}
						fieldProps={{
							url: {
								value: recordUrl,
								disabled: true,
							},
							permissions: {
								isAdmin,
							},
							category: {
								categoryGroup: "SP_ADHOC"
							},
							linkedTasks: {
								onCreateTaskClick: () => {
									let path = `${recordUrl}/task/create`;
									if (taskId) {
										path = `${path}?milestoneId=${taskId}`;
									}
									window.open(path, "_blank");
								},
							},
						}}
						allowToRemoveAttachmentsByRoles={ALLOW_TO_EDIT_ACTIONS_BY_ROLES}
						allowToRemoveCommentsByRoles={ALLOW_TO_EDIT_ACTIONS_BY_ROLES}
						type="SP_ADHOC"
						renderActions={component => {
							const container = document.querySelector("div.layout");
							const portalContent = taskId ? (
								<div className={styles.milestoneHeaderActions}>
									<MilestoneProgress />
									{component}
								</div>
							) : component;

							return container ? createPortal(portalContent, container) : null;
						}}
					/>
				</div>
			</BasePadding>
			<AlertDialog
				title={dictionary.unsavedChangesAlertDialog.title ?? "Leave Page?"}
				description={dictionary.unsavedChangesAlertDialog.description ?? "Changes you made will not be saved."}
				isOpen={isShowConfirmCancelModal}
				onConfirm={redirectBack}
				onCancel={onAbortCancel}
				confirmText={dictionary.unsavedChangesAlertDialog.confirmText ?? "Leave page"}
			/>
		</div>
	);
}
